import type { ScatterDataPoint } from 'chart.js';
import { basePalette } from '../../../../../../../design-system';
import { LLMClient } from '../../../../../../../utils/chargePoints/diagnostics';
import { ThreePhase } from '../../../../../../../utils/meterValues';
import { ClientMeasurement, ClientMeasurementAttributes } from './ClientMeasurement';
import { getDataset, LineDataset } from './presentation';

export type ClientName = string;

export type ClientsByName = Record<ClientName, LLMClientModel>;

interface LLMClientAttributes {
  label: ClientName;
  measurements: ClientMeasurementAttributes[];
}

export const llmColourOptions = [
  basePalette.blue.main,
  basePalette.steel.main,
  basePalette.purple.main,
  basePalette.amber.main,
  basePalette.cyan.main,
  '#abcf38',
  '#E9C46A',
  '#889696',
  '#E76F51',
  '#278577',
  '#3E2F5B',
  '#236477',
  '#D7907B',
  '#011627',
  '#4cc9f0',
  '#432818',
  '#f20089',
];

export class LLMClientModel implements LLMClientAttributes {
  readonly label: ClientName;

  readonly measurements: ClientMeasurement[];

  private constructor(params: LLMClientAttributes) {
    const { label, measurements } = params;
    this.label = label;
    this.measurements = measurements.map((c) => new ClientMeasurement(c));
  }

  static createFromData(params: LLMClientAttributes) {
    return new LLMClientModel(params);
  }

  static addClientModel(args: {
    currentClient: LLMClientModel | undefined;
    llmClient: LLMClient;
    serverEventDate: string;
    allClients: ClientsByName;
  }): ClientsByName {
    const { allClients, llmClient, serverEventDate } = args;
    const { Name, MeasuredCurrent: current, ChargingOnPhase: phase } = llmClient;
    const measurement = new ClientMeasurement({ current, phase, serverEventDate });

    const currentClient = args.currentClient ?? allClients[Name];
    const clientModel = currentClient
      ? currentClient.addMeasurement(measurement)
      : LLMClientModel.createFromData({ label: Name, measurements: [measurement] });

    return { ...allClients, [Name]: clientModel };
  }

  static getDatasets(args: { clientsByName: ClientsByName; phase: ThreePhase }): LineDataset[] {
    const { clientsByName, phase } = args;
    return Object.keys(clientsByName)
      .sort((a, b) => a.localeCompare(b))
      .reduce<LineDataset[]>((datasets, label, index) => {
        const client = clientsByName[label];
        return [...datasets, client.getDataset({ colour: llmColourOptions[index], label, phase })];
      }, []);
  }

  private getDataset(args: { colour: string; label: string; phase: ThreePhase }): LineDataset {
    const { colour, label, phase } = args;
    const dataset = getDataset({
      key: 'client',
      data: this.getSortedData({ phase }),
    });
    return { ...dataset, label, borderColor: colour, backgroundColor: colour };
  }

  addMeasurement(value: ClientMeasurement): LLMClientModel {
    return new LLMClientModel({ ...this, measurements: [...this.measurements, value] });
  }

  getSortedData(args: { phase: ThreePhase }): ScatterDataPoint[] {
    const { phase } = args;
    return this.measurements.map((model) => model.getDatum({ phase })).sort((a, b) => (a && b ? a.x - b.x : 0));
  }
}
